import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { DEFAULT_SELECTED_SUBPUBLISHER_CATALOG } from '~/modules/retailMedia/constants/subpublisherCatalogs'
import {
  getPublishersOnsite,
  getPublishersOnsiteKey
} from '~/modules/retailMedia/services/publisher'

import { useCampaignForm } from '../CampaignForm/hooks/useCampaignForm'

// Stable reference (not `[]` inline) so it doesn't change identity every
// render and break the useMemo below that depends on selectedSubpublishers.
const EMPTY_SUBPUBLISHERS: PublisherId[] = []

export function useSubpublisherOptions({
  formRef,
  campaignTypeOverride,
  destinationPublisherIdsOverride
}: UseProductsPanelSubpublisherProps) {
  const {
    selectedSubpublishers: formSelectedSubpublishers,
    allSubpublishers: formAllSubpublishers,
    campaignType: formCampaignType
  } = useCampaignForm()

  // Telas de edição (fora do wizard de criação) não têm CampaignFormProvider,
  // então recebem o retailer de destino real da própria campanha.
  const campaignType = campaignTypeOverride ?? formCampaignType
  // formSelectedSubpublishers is undefined outside CampaignFormProvider (the
  // edit route isn't wrapped in it) — fall back to [] so selectedSubpublishers[0]
  // below never throws, e.g. while destinationPublishers hasn't loaded yet.
  const selectedSubpublishers =
    destinationPublisherIdsOverride ??
    formSelectedSubpublishers ??
    EMPTY_SUBPUBLISHERS

  // Resolves publisher_id to a display name for destinationPublisherIdsOverride.
  const { data: onsitePublishers } = useQuery({
    queryKey: [getPublishersOnsiteKey],
    queryFn: async () => {
      const firstPage = await getPublishersOnsite({})
      const totalPages = firstPage?.data?.pages
      const promises = []

      if (!!totalPages && totalPages > 1) {
        for (let i = 2; i <= totalPages; i++) {
          promises.push(getPublishersOnsite({ page: i }))
        }
      }

      const results = await Promise.all(promises)

      return [
        ...(firstPage?.data?.data ?? []),
        ...(results?.flatMap(r => r?.data?.data) ?? [])
      ]
    },
    enabled: !!destinationPublisherIdsOverride?.length
  })

  const allSubpublishers = useMemo(() => {
    if (!destinationPublisherIdsOverride) return formAllSubpublishers

    const resolveName = (id: string) =>
      onsitePublishers?.find(p => p.publisher_id === id)?.publisher_name ?? id

    return destinationPublisherIdsOverride.map(id => ({
      id,
      name: resolveName(id)
    }))
  }, [destinationPublisherIdsOverride, formAllSubpublishers, onsitePublishers])

  const isOffSite = campaignType === 'off_site'

  const defaultSelectedPublisher = useMemo(
    () =>
      (isOffSite && selectedSubpublishers[0]) ||
      DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.id,
    [isOffSite, selectedSubpublishers]
  )

  const [selectedSubpublisherCatalog, setSelectedSubpublisherCatalog] =
    useState<string>(defaultSelectedPublisher)

  // Hooks below must always run (rules-of-hooks) — the non-off_site case is
  // handled by gating each computed value instead of an early return.
  const subPublishersCatalogOptions = useMemo(() => {
    if (!isOffSite) return []

    const data = formRef?.current?.getData()
    const subpublishersCheck = data?.subpublishers?.subpublishersCheck

    if (subpublishersCheck === 'selected' || isOffSite)
      return selectedSubpublishers
    else return allSubpublishers?.map(item => item.id)
  }, [isOffSite, selectedSubpublishers, allSubpublishers, formRef?.current])

  const subPublisherIds = useMemo(
    () =>
      isOffSite &&
      !!selectedSubpublisherCatalog &&
      selectedSubpublisherCatalog !== DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.id
        ? selectedSubpublisherCatalog
        : undefined,
    [isOffSite, selectedSubpublisherCatalog]
  )

  const shouldShowSubpublishersCatalogFilter = useMemo(
    () =>
      isOffSite &&
      !!subPublishersCatalogOptions &&
      subPublishersCatalogOptions.length > 1,
    [isOffSite, subPublishersCatalogOptions]
  )

  useEffect(() => {
    setSelectedSubpublisherCatalog(defaultSelectedPublisher)
  }, [defaultSelectedPublisher])

  return {
    allSubpublishers,
    selectedSubpublisherCatalog,
    setSelectedSubpublisherCatalog,
    subPublishersCatalogOptions,
    subPublisherIds,
    shouldShowSubpublishersCatalogFilter,
    campaignType
  }
}
